import React, { useState } from "react";
import classes from "./MedicalInfo.module.css";

const MedicalInfo = ({ onPrevious, onNext, formData, setFormData }) => {
  const [errors, setErrors] = useState({});
  const [allergyList, setAllergyList] = useState([]);
  const [medicationList, setMedicationList] = useState([]);
  const [surgeryList, setSurgeryList] = useState([]);

  const reactionsList = ["Rash", "Swelling", "Anaphylaxis", "Nausea", "Itching"];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleAddAllergy = () => {
    if (!formData.allergyName || !formData.reaction) return;
    setAllergyList([...allergyList, { allergy: formData.allergyName, reaction: formData.reaction }]);
    setFormData((prev) => ({ ...prev, allergyName: "", reaction: "" }));
  };

  const handleAddMedication = () => {
    if (!formData.medicationName || !formData.dosage || !formData.frequency || !formData.route) return;
    setMedicationList([...medicationList, { 
      name: formData.medicationName, 
      dosage: formData.dosage, 
      frequency: formData.frequency, 
      route: formData.route 
    }]);
    setFormData((prev) => ({ 
      ...prev, 
      medicationName: "", 
      dosage: "", 
      frequency: "", 
      route: "" 
    }));
  };

  const handleAddSurgery = () => {
    if (!formData.procedureName || !formData.surgeryDate) return;
    setSurgeryList([...surgeryList, { 
      procedure: formData.procedureName, 
      date: formData.surgeryDate, 
      hospital: formData.hospital 
    }]);
    setFormData((prev) => ({ 
      ...prev, 
      procedureName: "", 
      surgeryDate: "", 
      hospital: "" 
    }));
  };

  const calculateBMI = () => {
    const weight = parseFloat(formData.weight);
    const height = parseFloat(formData.height);

    if (!weight || !height) return "";

    // Height is in cm, convert to meters for BMI calculation
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);
    return bmi.toFixed(1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Let parent handle validation
    onNext();
  };

  return (
    <div className={classes.container}>
      <form onSubmit={handleSubmit} className={classes.form}>
        
        {/* Vitals */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Vitals</h3>
          <p className={classes.sectionSubtitle}>Enter the patient's vital statistics</p>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Blood Group <span className={classes.required}>*</span></label>
              <select 
                name="blood_group" 
                className={`${classes.formSelect} ${errors.blood_group ? classes.inputError : ""}`} 
                value={formData.blood_group || ""} 
                onChange={handleChange}
              >
                <option value="">Select blood group</option>
                <option>A+</option>
                <option>A-</option>
                <option>B+</option>
                <option>B-</option>
                <option>AB+</option>
                <option>AB-</option>
                <option>O+</option>
                <option>O-</option>
              </select>
              {errors.blood_group && <span className={classes.errorText}>{errors.blood_group}</span>}
            </div>

            <div className={classes.inputGroup}>
              <label>Genotype <span className={classes.required}>*</span></label>
              <select 
                name="genotype" 
                className={`${classes.formSelect} ${errors.genotype ? classes.inputError : ""}`} 
                value={formData.genotype || ""} 
                onChange={handleChange}
              >
                <option value="">Select genotype</option>
                <option>AA</option>
                <option>AS</option>
                <option>AC</option>
                <option>SS</option>
                <option>SC</option>
              </select>
              {errors.genotype && <span className={classes.errorText}>{errors.genotype}</span>}
            </div>
          </div>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Height (in cm) <span className={classes.required}>*</span></label>
              <input 
                type="number" 
                name="height" 
                placeholder="Enter height in cm (e.g., 175)" 
                className={classes.formInput} 
                value={formData.height || ""} 
                onChange={handleChange} 
                step="0.01"
                min="0"
              />
            </div>

            <div className={classes.inputGroup}>
              <label>Weight (in kg) <span className={classes.required}>*</span></label>
              <input 
                type="number" 
                name="weight" 
                placeholder="Enter weight in kg (e.g., 70)" 
                className={classes.formInput} 
                value={formData.weight || ""} 
                onChange={handleChange} 
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className={classes.inputGroup}>
            <label>BMI</label>
            <input type="text" value={calculateBMI()} placeholder="Auto calculated" readOnly className={classes.formInputReadOnly} />
          </div>
        </div>

        {/* Medical Conditions */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Medical Conditions</h3>
          <div className={classes.inputGroup}>
            <label>Existing Medical Conditions</label>
            <input 
              type="text" 
              name="existingConditions" 
              placeholder="Enter medical conditions (e.g., Hypertension, Diabetes, Ulcer)" 
              className={classes.formInput} 
              value={formData.existingConditions || ""} 
              onChange={handleChange} 
            />
          </div>
          <label className={classes.checkboxLabel}>
            <input type="checkbox" name="noKnownConditions" checked={formData.noKnownConditions || false} onChange={handleChange} />
            No Known Medical Conditions
          </label>
        </div>

        {/* Allergies */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Allergies</h3>
          
          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Allergies</label>
              <input 
                type="text" 
                name="allergyName" 
                placeholder="Enter allergies (e.g., Penicillin, Peanuts, Latex)" 
                className={classes.formInput} 
                value={formData.allergyName || ""} 
                onChange={handleChange} 
              />
            </div>
            
            <div className={classes.inputGroup}>
              <label>Reaction</label>
              <input 
                type="text" 
                name="reaction" 
                placeholder="Enter reactions (e.g., Rash, Swelling, Anaphylaxis)" 
                className={classes.formInput} 
                value={formData.reaction || ""} 
                onChange={handleChange} 
              />
            </div>
          </div>

          <label className={classes.checkboxLabel}>
            <input type="checkbox" name="noKnownAllergies" checked={formData.noKnownAllergies || false} onChange={handleChange} />
            No Known Allergies
          </label>

          <button type="button" className={classes.addBtn} onClick={handleAddAllergy}>+ Add Allergy</button>
          {allergyList.length > 0 && (
            <div className={classes.listContainer}>
              {allergyList.map((item, idx) => (
                <div key={idx} className={classes.listItem}>
                  <span>{item.allergy}</span>
                  <span className={classes.listMeta}>{item.reaction}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current Medications */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Current Medications</h3>
          <div className={classes.radioGroup}>
            <label>Currently Taking Medication?</label>
            <div className={classes.radioRow}>
              <label className={classes.radioLabel}>
                <input type="radio" name="takingMedication" value="Yes" checked={formData.takingMedication === "Yes"} onChange={handleChange} /> Yes
              </label>
              <label className={classes.radioLabel}>
                <input type="radio" name="takingMedication" value="No" checked={formData.takingMedication === "No"} onChange={handleChange} /> No
              </label>
            </div>
          </div>

          {formData.takingMedication === "Yes" && (
            <>
              <div className={classes.row2Col}>
                <div className={classes.inputGroup}>
                  <label>Medication Name <span className={classes.required}>*</span></label>
                  <input type="text" name="medicationName" placeholder="Enter medication name" className={classes.formInput} value={formData.medicationName || ""} onChange={handleChange} />
                </div>
                <div className={classes.inputGroup}>
                  <label>Dosage <span className={classes.required}>*</span></label>
                  <input type="text" name="dosage" placeholder="Enter dosage" className={classes.formInput} value={formData.dosage || ""} onChange={handleChange} />
                </div>
              </div>

              <div className={classes.row2Col}>
                <div className={classes.inputGroup}>
                  <label>Frequency <span className={classes.required}>*</span></label>
                  <select name="frequency" className={classes.formSelect} value={formData.frequency || ""} onChange={handleChange}>
                    <option value="">Select frequency</option>
                    <option>Once daily</option>
                    <option>Twice daily</option>
                    <option>Three times daily</option>
                    <option>As needed</option>
                  </select>
                </div>
                <div className={classes.inputGroup}>
                  <label>Route <span className={classes.required}>*</span></label>
                  <select name="route" className={classes.formSelect} value={formData.route || ""} onChange={handleChange}>
                    <option value="">Select route</option>
                    <option>Oral</option>
                    <option>Intravenous</option>
                    <option>Intramuscular</option>
                    <option>Topical</option>
                  </select>
                </div>
              </div>

              <button type="button" className={classes.addBtn} onClick={handleAddMedication}>+ Add Medication</button>
              {medicationList.length > 0 && (
                <div className={classes.listContainer}>
                  {medicationList.map((item, idx) => (
                    <div key={idx} className={classes.listItem}>
                      <span>{item.name}</span>
                      <span className={classes.listMeta}>{item.dosage} • {item.frequency}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Surgical History */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Surgical History</h3>
          <div className={classes.radioGroup}>
            <label>Previous Surgery?</label>
            <div className={classes.radioRow}>
              <label className={classes.radioLabel}>
                <input type="radio" name="previousSurgery" value="Yes" checked={formData.previousSurgery === "Yes"} onChange={handleChange} /> Yes
              </label>
              <label className={classes.radioLabel}>
                <input type="radio" name="previousSurgery" value="No" checked={formData.previousSurgery === "No"} onChange={handleChange} /> No
              </label>
            </div>
          </div>

          {formData.previousSurgery === "Yes" && (
            <>
              <div className={classes.row2Col}>
                <div className={classes.inputGroup}>
                  <label>Procedure Name <span className={classes.required}>*</span></label>
                  <input type="text" name="procedureName" placeholder="Enter procedure" className={classes.formInput} value={formData.procedureName || ""} onChange={handleChange} />
                </div>
                <div className={classes.inputGroup}>
                  <label>Date of Surgery <span className={classes.required}>*</span></label>
                  <input type="date" name="surgeryDate" className={classes.formInput} value={formData.surgeryDate || ""} onChange={handleChange} />
                </div>
              </div>

              <div className={classes.inputGroup}>
                <label>Hospital / Facility</label>
                <input type="text" name="hospital" placeholder="Enter hospital/facility" className={classes.formInput} value={formData.hospital || ""} onChange={handleChange} />
              </div>

              <button type="button" className={classes.addBtn} onClick={handleAddSurgery}>+ Add Surgery</button>
              {surgeryList.length > 0 && (
                <div className={classes.listContainer}>
                  {surgeryList.map((item, idx) => (
                    <div key={idx} className={classes.listItem}>
                      <span>{item.procedure}</span>
                      <span className={classes.listMeta}>{item.date}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Additional Medical Information */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Additional Medical Information</h3>
          <div className={classes.inputGroup}>
            <label>Past Medical History</label>
            <textarea 
              name="past_med_history" 
              placeholder="Enter relevant past medical history..." 
              className={classes.formTextarea} 
              rows="3" 
              value={formData.past_med_history || ""} 
              onChange={handleChange} 
            />
          </div>
          <div className={classes.inputGroup}>
            <label>Family Medical History</label>
            <textarea 
              name="family_med_history" 
              placeholder="Enter relevant family medical history..." 
              className={classes.formTextarea} 
              rows="3" 
              value={formData.family_med_history || ""} 
              onChange={handleChange} 
            />
          </div>
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <div className={classes.note}>
            Medical information helps healthcare professionals provide safe and appropriate care.
          </div>
          <div className={classes.footerButtons}>
            <button type="button" className={classes.previousBtn} onClick={onPrevious}>← Previous</button>
            <button type="submit" className={classes.nextBtn}>Next →</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MedicalInfo;